import { queryOptions } from "@tanstack/react-query";

import type { SellerThreadDetail, SellerThreadList } from "@recruiter/shared";

import { queryClient } from "../query-client";
import {
  fetchSellerInsights,
  fetchSellerOverview,
  fetchSellerPreferences,
  fetchSellerProjects,
  fetchSellerThread,
  fetchSellerThreads,
  type SellerFetchOutcome,
} from "../seller-client";

// The single place talent cache entries are addressed, so the Pusher→cache
// dispatcher and the component reads reference the SAME keys (no key drift — the
// #1 cause of "the event lands but the UI doesn't update"). The directory is
// identity-free; the thread list/detail carry the sellerId so switching talent
// re-keys the read (mirrors web's `workspaceKeys.detail(projectId)`).
export const sellerKeys = {
  all: ["seller"] as const,
  projects: () => ["seller", "projects"] as const,
  threads: (sellerId: string) => ["seller", "threads", sellerId] as const,
  thread: (sellerId: string, threadId: string) => ["seller", "thread", sellerId, threadId] as const,
  // Prefix of every chat this talent has open (marked stale together when the tester leaves them).
  threadDetails: (sellerId: string) => ["seller", "thread", sellerId] as const,
  // Prefix of every overview this talent has open (invalidated together after a
  // preferences save: each carries a fit line computed from them).
  overviews: (sellerId: string) => ["seller", "overview", sellerId] as const,
  overview: (sellerId: string, threadId: string) =>
    ["seller", "overview", sellerId, threadId] as const,
  preferences: (sellerId: string) => ["seller", "preferences", sellerId] as const,
  insights: (sellerId: string) => ["seller", "insights", sellerId] as const,
};

// The project overview's funnel counts OTHER talents' progress, which arrives on THEIR
// channels, never this one. So instead of the push-only `Infinity`, an overview goes stale
// after this long and re-reads when the talent opens the project again. A remount refetch,
// not a poll: nothing re-reads while the view sits open (doctrine 1.2).
export const OVERVIEW_STALE_MS = 30_000;

// The thread list and the open chat are the same problem. Pusher keeps the talent ON SCREEN fresh,
// but the app subscribes to that one channel only, so a message that lands for another talent while
// the tester is away is never seen, and `Infinity` showed the old chat on switching back until a
// reload (Monday 3230136082). Same remedy as the overview: a finite clock, re-read when the talent
// is opened again and never while a view sits open (doctrine 1.2). The clock alone leaves a window
// for a quick switch back, which `markTalentChatsStale` closes.
export const THREAD_STALE_MS = 30_000;

// The fetchers in seller-client.ts deliberately never throw (they return a typed
// outcome). TanStack needs a throw to drive `query.error`, so unwrap here: the
// typed `status` + the human `message` survive on SellerQueryError, which
// components render through SellerErrorState (doctrine 1.5 — surface, never swallow).
export class SellerQueryError extends Error {
  constructor(
    readonly status: 401 | 403 | 404 | "error",
    message: string,
  ) {
    super(message);
    this.name = "SellerQueryError";
  }
}

export function unwrap<T>(outcome: SellerFetchOutcome<T>): T {
  if (outcome.ok) return outcome.data;
  throw new SellerQueryError(outcome.status, outcome.message);
}

// --- queryOptions factories (TkDodo's pattern — reusable in useQuery and the
// dispatcher's cache ops; NOT a useXQuery wrapper). `staleTime: Infinity` because
// Pusher drives freshness: the queries fetch once on mount (these are navigational
// REST resources) and the dispatcher patches/invalidates thereafter. The exceptions
// are the thread list, the open chat and the overview, which go stale on a clock
// because Pusher only carries the talent on screen. -----------

export function projectsQueryOptions() {
  return queryOptions({
    queryKey: sellerKeys.projects(),
    queryFn: async () => unwrap(await fetchSellerProjects()),
    staleTime: Infinity,
  });
}

export function threadListQueryOptions(sellerId: string) {
  return queryOptions({
    queryKey: sellerKeys.threads(sellerId),
    queryFn: async () => unwrap(await fetchSellerThreads(sellerId)),
    // Never fetch without an identity — an empty x-seller-id 401s.
    enabled: sellerId.length > 0,
    staleTime: THREAD_STALE_MS,
  });
}

export function threadDetailQueryOptions(sellerId: string, threadId: string | null) {
  return queryOptions({
    queryKey: sellerKeys.thread(sellerId, threadId ?? ""),
    // queryFn only runs when `enabled` is true (both ids present), so the cast is safe.
    queryFn: async () => unwrap(await fetchSellerThread(threadId as string, sellerId)),
    enabled: sellerId.length > 0 && !!threadId,
    staleTime: THREAD_STALE_MS,
  });
}

export function overviewQueryOptions(sellerId: string, threadId: string | null) {
  return queryOptions({
    queryKey: sellerKeys.overview(sellerId, threadId ?? ""),
    queryFn: async () => unwrap(await fetchSellerOverview(threadId as string, sellerId)),
    enabled: sellerId.length > 0 && !!threadId,
    staleTime: OVERVIEW_STALE_MS,
  });
}

// Pro insights set the talent against OTHER talents on the same projects, whose progress
// never arrives on this channel: the overview's clock, read when the panel opens and never
// while it sits open (doctrine 1.2).
export function insightsQueryOptions(sellerId: string) {
  return queryOptions({
    queryKey: sellerKeys.insights(sellerId),
    queryFn: async () => unwrap(await fetchSellerInsights(sellerId)),
    enabled: sellerId.length > 0,
    staleTime: OVERVIEW_STALE_MS,
  });
}

export function preferencesQueryOptions(sellerId: string) {
  return queryOptions({
    queryKey: sellerKeys.preferences(sellerId),
    queryFn: async () => unwrap(await fetchSellerPreferences(sellerId)),
    enabled: sellerId.length > 0,
    staleTime: Infinity,
  });
}

// --- single-writer cache helpers (mirror web's `updateWorkspaceCache`). Both
// NO-OP when their entry is absent: unlike web's always-present workspace, a talent
// thread detail/list has no safe empty shape, so never fabricate a partial — the
// open-on-mount useQuery fetches the authoritative copy instead. ----------------

export function updateThreadDetailCache(
  sellerId: string,
  threadId: string,
  updater: (current: SellerThreadDetail) => SellerThreadDetail,
): void {
  queryClient.setQueryData<SellerThreadDetail>(sellerKeys.thread(sellerId, threadId), (current) =>
    current ? updater(current) : current,
  );
}

export function updateThreadListCache(
  sellerId: string,
  updater: (current: SellerThreadList) => SellerThreadList,
): void {
  queryClient.setQueryData<SellerThreadList>(sellerKeys.threads(sellerId), (current) =>
    current ? updater(current) : current,
  );
}

// Called as the tester LEAVES a talent (`watchTalentSwitches`). Pusher carries only the talent on
// screen, so whatever lands for this one from now on is never seen. Marking its list and every chat
// it had open stale WITHOUT fetching (nobody is looking at them) makes opening the talent again
// re-read them once, however soon that is. A mark, not a refetch: nothing re-reads while a view
// sits open (doctrine 1.2).
export function markTalentChatsStale(sellerId: string): void {
  if (!sellerId) return; // no identity (the directory): nothing to mark
  for (const queryKey of [sellerKeys.threads(sellerId), sellerKeys.threadDetails(sellerId)]) {
    void queryClient.invalidateQueries({ queryKey, refetchType: "none" });
  }
}
